/**
 * UI State Store (Zustand)
 *
 * Global UI state management:
 * - Modal visibility
 * - Toast notifications
 * - Theme preferences
 * - User preferences
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

interface UIState {
  // Modal state
  activeModal: string | null;

  // Toast state (if not using react-hot-toast, can use this)
  toasts: Array<{
    id: string;
    message: string;
    type: "success" | "error" | "warning" | "info";
  }>;

  // Theme preferences (for future use)
  theme: "light" | "dark" | "auto";

  // User preferences (persisted)
  preferences: {
    // Add user preferences here as needed
    autoSaveEnabled: boolean;
    notificationsEnabled: boolean;
  };

  // Actions - Modal
  openModal: (modalId: string) => void;
  closeModal: () => void;
  closeAllModals: () => void;

  // Actions - Toast
  addToast: (
    message: string,
    type?: "success" | "error" | "warning" | "info"
  ) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Actions - Theme
  setTheme: (theme: "light" | "dark" | "auto") => void;

  // Actions - Preferences
  updatePreferences: (preferences: Partial<UIState["preferences"]>) => void;
  resetPreferences: () => void;
}

const defaultPreferences = {
  autoSaveEnabled: true,
  notificationsEnabled: true,
};

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      activeModal: null,
      toasts: [],
      theme: "auto",
      preferences: defaultPreferences,

      // Modal actions
      openModal: (modalId) => set({ activeModal: modalId }),

      closeModal: () => set({ activeModal: null }),

      closeAllModals: () => set({ activeModal: null }),

      // Toast actions
      addToast: (message, type = "info") => {
        const id = Math.random().toString(36).substring(7);
        set((state) => ({
          toasts: [...state.toasts, { id, message, type }],
        }));

        // Auto-remove toast after 5 seconds
        setTimeout(() => {
          get().removeToast(id);
        }, 5000);
      },

      removeToast: (id) =>
        set((state) => ({
          toasts: state.toasts.filter((toast) => toast.id !== id),
        })),

      clearToasts: () => set({ toasts: [] }),

      // Theme actions
      setTheme: (theme) => set({ theme }),

      // Preferences actions
      updatePreferences: (newPreferences) =>
        set((state) => ({
          preferences: {
            ...state.preferences,
            ...newPreferences,
          },
        })),

      resetPreferences: () => set({ preferences: defaultPreferences }),
    }),
    {
      name: "ui-preferences-storage",
      partialize: (state) => ({
        theme: state.theme,
        preferences: state.preferences,
      }),
    }
  )
);
